import { useState, useEffect } from 'react';
import { PlatformLayout } from '../components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { Search, Eye, ToggleLeft, ToggleRight, Brain, Users, Database, Building2, ChevronLeft, Mic, Globe, Sparkles, Zap, FileText, Phone } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return '-'; }
}

function SyncStatusBadge({ status }) {
  if (status === 'ok') return <Badge className="bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[11px]">OK</Badge>;
  if (status === 'error') return <Badge variant="destructive" className="text-[11px]">Erreur</Badge>;
  return <Badge variant="secondary" className="text-[11px]">Jamais</Badge>;
}

export default function PlatformTenantsPage() {
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await axios.get(`${API}/platform/tenants`, { headers: { Authorization: `Bearer ${token}` }, params });
      setTenants(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTenants();
  };

  const toggleActive = async (tenantId, currentActive) => {
    try {
      await axios.patch(`${API}/platform/tenants/${tenantId}`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { active: !currentActive },
      });
      toast.success(`Tenant ${!currentActive ? 'active' : 'desactive'}`);
      fetchTenants();
      if (detail?.tenant?.id === tenantId) openDetail(tenantId);
    } catch (e) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const openDetail = async (tenantId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axios.get(`${API}/platform/tenants/${tenantId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDetail(res.data);
    } catch (e) {
      toast.error('Impossible de charger le detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleFeature = async (tenantId, feature, enabled) => {
    try {
      await axios.patch(`${API}/platform/tenants/${tenantId}/features`, { [feature]: enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${feature} ${enabled ? 'active' : 'desactive'}`);
      openDetail(tenantId);
    } catch (e) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  return (
    <PlatformLayout title="Tenants">
      <div className="space-y-4" data-testid="platform-tenants-page">
        {!detail ? (
          <>
            {/* Search */}
            <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Rechercher un tenant..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-9 h-9 text-sm" data-testid="tenant-search-input" />
              </div>
              <Button type="submit" size="sm" variant="outline" data-testid="tenant-search-btn">Rechercher</Button>
            </form>

            {/* Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Nom</TableHead>
                      <TableHead className="text-xs">Slug</TableHead>
                      <TableHead className="text-xs text-center">Utilisateurs</TableHead>
                      <TableHead className="text-xs text-center">Clones</TableHead>
                      <TableHead className="text-xs text-center">Memories</TableHead>
                      <TableHead className="text-xs">Statut</TableHead>
                      <TableHead className="text-xs">Cree le</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-muted-foreground">Chargement...</TableCell></TableRow>
                    ) : tenants.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-muted-foreground">Aucun tenant</TableCell></TableRow>
                    ) : tenants.map(t => (
                      <TableRow key={t.id} data-testid={`tenant-row-${t.id}`}>
                        <TableCell className="font-medium text-sm">{t.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">{t.slug}</TableCell>
                        <TableCell className="text-center text-sm">{t.users_count ?? 0}</TableCell>
                        <TableCell className="text-center text-sm">{t.clones_count ?? 0}</TableCell>
                        <TableCell className="text-center text-sm">{t.memories_count ?? 0}</TableCell>
                        <TableCell>
                          <Badge className={t.is_active !== false ? 'bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[11px]' : 'bg-red-600/15 text-red-700 border-red-200 text-[11px]'}>
                            {t.is_active !== false ? 'Actif' : 'Inactif'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDate(t.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDetail(t.id)}
                              title="Detail" data-testid={`tenant-detail-${t.id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleActive(t.id, t.is_active !== false)}
                              title={t.is_active !== false ? 'Desactiver' : 'Activer'} data-testid={`tenant-toggle-${t.id}`}>
                              {t.is_active !== false ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          /* Detail View */
          <div className="space-y-5" data-testid="tenant-detail-view">
            <Button variant="ghost" size="sm" onClick={() => setDetail(null)} className="gap-2" data-testid="tenant-detail-back">
              <ChevronLeft className="w-4 h-4" /> Retour
            </Button>

            {detailLoading ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Chargement...</p>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{detail.tenant?.name}</h2>
                    <p className="text-xs text-muted-foreground font-mono">{detail.tenant?.slug} &middot; ID: {detail.tenant?.id}</p>
                  </div>
                  <Badge className={detail.tenant?.is_active !== false ? 'bg-emerald-600/15 text-emerald-700 text-xs' : 'bg-red-600/15 text-red-700 text-xs'}>
                    {detail.tenant?.is_active !== false ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>

                {/* Mini stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { label: 'Memories', value: detail.stats?.memories_total ?? 0, icon: Database, color: 'text-rose-500' },
                    { label: 'Contacts', value: detail.stats?.contacts_total ?? 0, icon: Users, color: 'text-amber-500' },
                    { label: 'Dossiers', value: detail.stats?.dossiers_total ?? 0, icon: Building2, color: 'text-blue-500' },
                    { label: 'Organisations', value: detail.stats?.organizations_total ?? 0, icon: Building2, color: 'text-teal-500' },
                  ].map((s, i) => (
                    <Card key={i}>
                      <CardContent className="pt-4 pb-3 flex items-center gap-3">
                        <s.icon className={`w-4 h-4 ${s.color}`} />
                        <div>
                          <p className="text-lg font-bold">{s.value}</p>
                          <p className="text-[11px] text-muted-foreground">{s.label}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Features Premium */}
                <Card data-testid="tenant-features-card">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" />Features Premium</CardTitle></CardHeader>
                  <CardContent className="space-y-3 pt-1">
                    {[
                      { key: 'voice_standard', label: 'Voix Standard', desc: 'TTS OpenAI', icon: Mic, color: 'text-blue-500' },
                      { key: 'voice_clone', label: 'Voix Clonee (Premium)', desc: 'ElevenLabs voice cloning', icon: Mic, color: 'text-violet-500' },
                      { key: 'multilingual_clone', label: 'Clone Multilingue', desc: 'Reponses multilingues', icon: Globe, color: 'text-emerald-500' },
                      { key: 'crm_sania', label: 'CRM SAN-IA', desc: 'Enrichissement contacts via CRM SAN-IA', icon: Zap, color: 'text-amber-500' },
                      { key: 'google_drive', label: 'Google Drive', desc: 'Synchronisation documents Google Drive', icon: FileText, color: 'text-sky-500' },
                      { key: 'dropbox', label: 'Dropbox', desc: 'Synchronisation documents Dropbox', icon: FileText, color: 'text-blue-600' },
                      { key: 'whatsapp', label: 'WhatsApp', desc: 'Connexion WhatsApp via QR code', icon: Phone, color: 'text-green-500' },
                    ].map(f => {
                      const features = detail.tenant?.features || {};
                      return (
                        <div key={f.key} className="flex items-center justify-between py-1.5 border-b last:border-0" data-testid={`feature-row-${f.key}`}>
                          <div className="flex items-center gap-2.5">
                            <f.icon className={`w-4 h-4 ${f.color}`} />
                            <div>
                              <p className="text-sm font-medium">{f.label}</p>
                              <p className="text-[11px] text-muted-foreground">{f.desc}</p>
                            </div>
                          </div>
                          <Switch
                            checked={!!features[f.key]}
                            onCheckedChange={(checked) => toggleFeature(detail.tenant.id, f.key, checked)}
                            data-testid={`feature-toggle-${f.key}`}
                          />
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Users */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Utilisateurs ({detail.users?.length || 0})</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Nom</TableHead>
                          <TableHead className="text-xs">Email</TableHead>
                          <TableHead className="text-xs">Role</TableHead>
                          <TableHead className="text-xs">Derniere connexion</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(detail.users || []).map((u, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm font-medium">{u.first_name} {u.last_name}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                            <TableCell><Badge variant="secondary" className="text-[10px]">{u.role}</Badge></TableCell>
                            <TableCell className="text-xs text-muted-foreground">{formatDate(u.last_login)}</TableCell>
                          </TableRow>
                        ))}
                        {(!detail.users || detail.users.length === 0) && (
                          <TableRow><TableCell colSpan={4} className="text-center py-4 text-xs text-muted-foreground">Aucun utilisateur</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Clones */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Clones ({detail.clones?.length || 0})</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Nom</TableHead>
                          <TableHead className="text-xs text-center">Memories</TableHead>
                          <TableHead className="text-xs">Cree le</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(detail.clones || []).map((c, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm font-medium">{c.first_name} {c.last_name}</TableCell>
                            <TableCell className="text-center text-sm">{c.memory_count ?? 0}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{formatDate(c.created_at)}</TableCell>
                          </TableRow>
                        ))}
                        {(!detail.clones || detail.clones.length === 0) && (
                          <TableRow><TableCell colSpan={3} className="text-center py-4 text-xs text-muted-foreground">Aucun clone</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Sources */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Sources ({detail.sources?.length || 0})</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Type</TableHead>
                          <TableHead className="text-xs">Label</TableHead>
                          <TableHead className="text-xs">Derniere synchro</TableHead>
                          <TableHead className="text-xs">Statut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(detail.sources || []).map((s, i) => (
                          <TableRow key={i}>
                            <TableCell><Badge variant="outline" className="text-[10px]">{s.type}</Badge></TableCell>
                            <TableCell className="text-xs">{s.label}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{formatDate(s.last_sync)}</TableCell>
                            <TableCell><SyncStatusBadge status={s.status} /></TableCell>
                          </TableRow>
                        ))}
                        {(!detail.sources || detail.sources.length === 0) && (
                          <TableRow><TableCell colSpan={4} className="text-center py-4 text-xs text-muted-foreground">Aucune source</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
